import { useRef, useState } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getCalendarEventFeedbackEmailDisabledReason } from 'lib/calendar';
import { getFormattedDate } from 'lib/common';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { ConfirmButton, ModalButtonWithForm, User } from 'components/common';
import {
  Checkbox,
  EmailMessageEditor,
  FormGroup
} from 'components/common/form';

import type { FormApi } from 'informed';
import type { EmailMessageTemplateContent } from 'shared-types';
import type { SendCalendarEventFeedbackEmailReq } from 'shared-types/requests';
import type {
  GetCalendarEventUsersRes,
  GetEmailTemplatesByTypeRes
} from 'shared-types/responses';
import { getCalendarEventDateString } from 'shared-helpers/calendar';
import { UsersListSelectedUsers } from 'components/users';

interface FormValues {
  email: EmailMessageTemplateContent;
  includeAlreadySent?: boolean;
}

interface Props {
  emailTemplates: GetEmailTemplatesByTypeRes['emailTemplates'];
  selectedUserIds?: number[];
  updateUsers: () => Promise<void>;
  user?: GetCalendarEventUsersRes['users'][0];
}

const CalendarEventParticipantsSendFeedbackEmail = ({
  emailTemplates,
  selectedUserIds,
  updateUsers,
  user
}: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const {
    language,
    messages,
    pageState: { calendarEvent },
    serverConfig: { adminUrl, featureFlags, timeZone },
    user: loggedInUser
  } = useReduxState();

  const [resendConfirmed, setResendConfirmed] = useState(false);

  const showAction =
    featureFlags.enableFeedbackForCalendarEvents &&
    calendarEvent.feedbackEnabled &&
    calendarEvent.hasFeedbackForm;
  if (!showAction) return null;

  // a queued user has not been added to the event and cannot give feedback
  if (user && user.status !== 'added') return null;

  const disabledReason = getCalendarEventFeedbackEmailDisabledReason({
    calendarEvent,
    messages,
    now,
    user
  });

  const feedbackUrl = `${adminUrl}/calendar/${calendarEvent.id}/feedback`;

  const templatesWithPlaceholders = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: {
      contactEmail: loggedInUser?.email,
      eventTitle: calendarEvent.title,
      eventTime: getCalendarEventDateString({
        startDate: new Date(calendarEvent.startDate),
        endDate: calendarEvent.endDate
          ? new Date(calendarEvent.endDate)
          : undefined,
        timeZone
      }),
      eventUrl: `${adminUrl}/calendar/${calendarEvent.id}`,
      feedbackUrl
    }
  });

  const defaultEmailTemplate = getDefaultEmailTemplate({
    language,
    emailTemplates: templatesWithPlaceholders
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const response = await api.post(
        `/admin/calendar-events/${calendarEvent.id}/feedback-form/send-email`,
        {
          emailContent: values.email,
          // sending to one user is always a deliberate choice, and a resend
          // has been confirmed in the modal before this point
          includeAlreadySent: user ? true : Boolean(values.includeAlreadySent),
          ...(user
            ? { userIds: [user.id] }
            : selectedUserIds?.length
            ? { userIds: selectedUserIds }
            : {})
        } satisfies SendCalendarEventFeedbackEmailReq
      );
      if (!response.data || !response.data.success) throw new Error();

      const { successfulSends } = response.data;
      const erroredEmailAddresses: string[] =
        response.data.erroredEmailAddresses || [];

      let notificationType: 'success' | 'warning' = 'success';
      let notificationMessage = user
        ? messages.eventCalendarFeedbackSendEmailSuccessSingle
        : getMessageWithPlaceholders({
            message: messages.eventCalendarFeedbackSendEmailSuccess,
            placeholders: { numberOfEmails: successfulSends }
          });

      if (!successfulSends && !erroredEmailAddresses.length) {
        // everyone the email would have gone to turned out to be unable to give
        // feedback, so reporting a successful send would be misleading
        notificationType = 'warning';
        notificationMessage =
          messages.eventCalendarFeedbackSendEmailNoRecipients;
      } else if (erroredEmailAddresses.length) {
        notificationType = 'warning';
        notificationMessage += `<br /><br />${messages.usersSendMessageError}`;

        erroredEmailAddresses.forEach((emailAddress) => {
          notificationMessage += `<br />${emailAddress}`;
        });
      }

      formApi.current.reset();
      dispatch.addNotice({ text: notificationMessage, type: notificationType });
      if (successfulSends) await updateUsers();
    } catch (err) {
      console.log(err);
      await dispatch.handleErrorDispatch(err);
    }
  };

  if (!defaultEmailTemplate) return null;

  // resending to a single participant is confirmed first, so the owner cannot
  // send the same request twice without noticing
  if (user?.feedbackEmailSentAt && !resendConfirmed) {
    return (
      <ConfirmButton
        actionText={messages.commonContinue}
        buttonIcon="paper-plane"
        buttonIsDropdown={true}
        buttonText={messages.eventCalendarFeedbackSendEmail}
        buttonTooltip={disabledReason}
        confirmText={
          <>
            {getMessageWithPlaceholders({
              message: messages.eventCalendarFeedbackEmailSentOn,
              placeholders: {
                date: getFormattedDate({ date: user.feedbackEmailSentAt })
              }
            })}
            {`. ${messages.eventCalendarFeedbackSendEmailAgainMessage}`}
          </>
        }
        confirmTitle={messages.eventCalendarFeedbackSendEmailAgain}
        disabled={Boolean(disabledReason)}
        onConfirm={() => {
          setResendConfirmed(true);
          return true;
        }}
      />
    );
  }

  return (
    <ModalButtonWithForm
      apiRef={formApi}
      buttonDisabled={Boolean(disabledReason)}
      buttonIcon="paper-plane"
      buttonIsDropdown={true}
      buttonProps={{ tooltip: disabledReason }}
      buttonText={messages.eventCalendarFeedbackSendEmail}
      initialValues={{
        email: {
          ...defaultEmailTemplate.email,
          buttonLink: feedbackUrl,
          selectedTemplateIdentifier: defaultEmailTemplate.identifier
        },
        includeAlreadySent: false
      }}
      modalActionText={messages.eventCalendarFeedbackSendEmail}
      modalAutoFocus={false}
      modalTitle={messages.eventCalendarFeedbackSendEmail}
      modalVisibleDefault={resendConfirmed}
      modalWidth="wide"
      onHide={() => setResendConfirmed(false)}
      onSubmit={handleSubmit}
    >
      {() => (
        <>
          {user ? (
            <FormGroup label={messages.commonRecipient}>
              <User enableProfileLink={true} user={user} />
            </FormGroup>
          ) : (
            <UsersListSelectedUsers
              allowSelf={true}
              onLoadedUsers={() => null}
              userSelectionParams={{
                excludeUsersWithoutEmail: true,
                selectedUserIds,
                userSelector: 'emails'
              }}
            />
          )}

          <EmailMessageEditor
            availableTemplates={templatesWithPlaceholders}
            id={`calendar-event-participants-send-feedback-email-${
              user?.id || 'all'
            }`}
            style={{ marginTop: 20 }}
          />

          {user ? null : (
            <Checkbox
              field="includeAlreadySent"
              id="calendar-event-participants-send-feedback-email-include-already-sent"
              label={messages.eventCalendarFeedbackSendEmailIncludeAlreadySent}
              style={{ marginTop: 20 }}
            />
          )}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventParticipantsSendFeedbackEmail;
